import { IconNames } from '@dixa/lib-shared-habla';

/** Available dashboard overview table column types. */
export enum QueuesOverviewColumnType {
  Agents = 'agents',
  AverageHandlingTime = 'averageHandlingTime',
  AverageWaitTime = 'averageWaitTime',
  Conversations = 'conversation',
  CurrentLongestWaitingTime = 'currentLongestWaitingTime',
  Favourite = 'favourite',
  HandledConversations = 'handledConversations',
  Name = 'name',
  ServiceLevelAgreement = 'serviceLevelAgreement',
  TvDashboard = 'tvDashboard',
}

/** The properties of a queues overview column. */
export interface QueuesOverviewColumn {
  /** The label text. */
  readonly label?: string;
  /** The icon name. */
  readonly icon?: IconNames;
  /** The help tooltip. */
  readonly help?: {
    /** The help tooltip headline text. */
    readonly headline: string;
    /** The help tooltip description text. */
    readonly description: string;
  };
  /** The type of the column. */
  readonly type: QueuesOverviewColumnType;
  /** Whether or not the column is hidden. */
  readonly hidden?: boolean;
  /** Optional styles that can be appended to the column cell. */
  readonly style?: React.CSSProperties;
}

/** Available dashboard overview table columns in a pre-defined order. */
const QUEUES_OVERVIEW_COLUMNS: ReadonlyArray<QueuesOverviewColumn> = [
  {
    type: QueuesOverviewColumnType.Favourite,
    style: {
      textAlign: 'center',
      paddingRight: 0,
      width: 54,
    },
  },
  {
    type: QueuesOverviewColumnType.Name,
    label: 'Name',
    style: {
      paddingLeft: 4,
    },
  },
  {
    type: QueuesOverviewColumnType.Agents,
    label: 'Agents',
    style: {
      width: '10%',
    },
  },
  {
    type: QueuesOverviewColumnType.Conversations,
    label: 'Conversations',
    style: {
      width: '20%',
    },
  },
  {
    type: QueuesOverviewColumnType.AverageWaitTime,
    label: 'AWT',
    help: {
      headline: 'Average Wait Time',
      description:
        'The average length of time between a conversation being offered to a queue and it being answered by an agent.',
    },
    style: {
      width: '13%',
    },
  },
  {
    type: QueuesOverviewColumnType.HandledConversations,
    label: 'Handled',
    hidden: true,
  },
  {
    type: QueuesOverviewColumnType.AverageHandlingTime,
    label: 'AHT',
    help: {
      headline: 'Average Handling Time',
      description:
        'The average length of time that it takes to complete a conversation between an agent and a customer.',
    },
    style: {
      width: '13%',
    },
  },
  {
    type: QueuesOverviewColumnType.ServiceLevelAgreement,
    label: 'SLA',
    help: {
      headline: 'Service-Level Agreement',
      description:
        '"Service-Level" is your goal for satisfactory time of answer on various conversation types.\n\n"The Service-Level" is defined as the percentage of conversations answered within a predefined amount of time.\n\nAn often used "Service-Level" is for instance: "_We want to answer 80 % of our support calls within 20 seconds_".',
    },
    style: {
      width: '10%',
    },
  },
  {
    type: QueuesOverviewColumnType.CurrentLongestWaitingTime,
    label: 'LWT',
    help: {
      headline: 'Current Longest Wait Time',
      description:
        'The longest time period that a customer has remained in a queue before connecting with an agent or hanging up.',
    },
    style: {
      width: '13%',
    },
  },
  {
    type: QueuesOverviewColumnType.TvDashboard,
    icon: 'dashboard',
    style: {
      textAlign: 'center',
      width: 54,
    },
  },
];

export const VISIBLE_QUEUES_OVERVIEW_COLUMNS: ReadonlyArray<QueuesOverviewColumn> =
  QUEUES_OVERVIEW_COLUMNS.filter((column) => !column.hidden);
